import { UniqueDocumentValidator } from "./../../../../../shared/validators/unique-document.validator";
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { IDLengthValidator } from "../../../../../shared/validators/id-length.validator";
import { RequiredValidator } from "../../../../../shared/validators/required.validator";
import { IDFormatValidator } from "../../../../../shared/validators/id-format.validator";
import { GTMService } from "src/app/shared/services/gtm.service";
import { FormService } from "src/app/shared/services/form.service";
import { InvestmentFieldsValidator } from "src/app/shared/validators/investment-fields.validator";
import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';

@Component({
  selector: "app-sigup-investor-form",
  templateUrl: "./sigup-investor-form.component.html",
  styleUrls: ["./sigup-investor-form.component.scss"],
})
export class SigupInvestorFormComponent implements OnInit {
  @Output() previousStep = new EventEmitter();
  @Output() nextStep = new EventEmitter();

  form: FormGroup;
  idOptions = [
    { value: "national id", text: "DNI" },
    { value: "foreigner card", text: "Carnet de Extranjería" },
    { value: "passport", text: "Pasaporte" },
    { value: "ruc", text: "RUC" },
  ];
  segments;
  channels;
  moneySource = [];
  investmentFields = [];
  moneySourceOther = "";
  countryOptions: any[] = [];

  checkBoxLabel =
    "No quiero <span class='bold'>recibir comunicaciones</span> con fines comerciales.";
  selectedIdType;

  constructor(
    private formBuilder: FormBuilder,
    public gtmService: GTMService,
    private formService: FormService,
    private uniqueDocumentValidator: UniqueDocumentValidator,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.channels = this.formService.getChannels();
    this.segments = this.formService.getInvestorSegments();
    this.moneySource = this.formService.getMoneySource();
    this.investmentFields = this.formService.getInvestmentFields();
    this.getCountryOptions();

    this.form = this.formBuilder.group({
      idType: ["", [Validators.required]],
      idNumber: [
        "",
        [RequiredValidator, IDFormatValidator, IDLengthValidator],
        [this.uniqueDocumentValidator.isUniqueDocument()],
      ],
      idFrontPicture: ["", [Validators.required]],
      idBackPicture: [""],
      channel: ["", [Validators.required]],
      segment: ["", [Validators.required]],
      isNewsletter: [""],
      investmentExperience: [true, [Validators.required]],
      investmentFields: ["", [InvestmentFieldsValidator]],
      moneySource: ["", [Validators.required]],
      country: ["", [Validators.required]],
    });

    this.form.get("investmentExperience").valueChanges.subscribe(() => {
      this.form.get("investmentFields").updateValueAndValidity();
    });
  }

  submit() {
    this.updateMoneySourceInput();

    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    const formValue = { ...this.form.value };

    formValue.isNewsletter = !formValue.isNewsletter;

    // Remove investmentFields if user has no investment experience
    if (!formValue.investmentExperience) {
      delete formValue.investmentFields;
    }

    // Remove fields that are not required based on LAFT and document type
    this.cleanPayload(formValue);

    if (this.form.valid) {
      this.nextStep.emit(formValue);
    } else {
      let event = {
        event: "[Platform][Next][Button]",
        _inputName: `Clicked Crear Mi Cuenta In Step 3 Error`,
        _inputCategory: `Signup Investor / Step 3`,
        _inputError: this.formService.getFormValidationErrors(this.form),
      };
      this.sendEvent(event);
    }
  }

  private cleanPayload(formValue: any) {
    const idType = formValue.idType || this.selectedIdType;
    const isRuc = idType === 'ruc';
    const isRetailWithLaft = !isRuc && this.isLAFTEnabled;
    const isRucWithLaftJuridica = isRuc && this.isLAFTJuridicaEnabled;

    // Remove ID pictures if LAFT is enabled for retail or always for RUC
    if (isRetailWithLaft || isRuc) {
      delete formValue.idFrontPicture;
      delete formValue.idBackPicture;
    }

    // Remove moneySource when LAFT is enabled:
    // 1. Retail investor with LAFT enabled
    // 2. RUC with LAFT Juridica enabled
    if (isRetailWithLaft || isRucWithLaftJuridica) {
      delete formValue.moneySource;
    }

    // Remove country/nationality for RUC (always, since RUC represents companies, not individuals)
    if (isRuc) {
      delete formValue.country;
    }
  }

  updateMoneySourceInput() {
    if (this.form.get("moneySource")?.value === "Otros") {
      this.form.patchValue({
        moneySource: this.moneySourceOther,
      });
    }
  }

  getCountryOptions() {
    this.formService.getAllCountries().subscribe((data: any) => {
      this.countryOptions = data.map((dep: any) => ({
        value: dep.nameSpanish,
        text: dep.nameSpanish,
      }));
    });
  }

  backward() {
    let event = {
      event: "[Platform][Next][Button]",
      _inputName: `Clicked Atrás In Step 3`,
      _inputCategory: `Signup Investor / Step 3`,
    };
    this.sendEvent(event);
    this.previousStep.emit();
  }

  idTypeChanged(newType) {
    this.form.patchValue({ idNumber: "" });
    this.selectedIdType = newType;

    // Clear country value if RUC is selected (since RUC doesn't need nationality)
    if (newType === 'ruc') {
      this.form.patchValue({ country: "" });
    }

    // Set validators for ID pictures based on document type and LAFT status
    this.setIdPictureValidators();

    // Set validators for money source
    this.setMoneySourceValidators();

    // Set validators for country/nationality
    this.setCountryValidators();

    // Update all form controls
    this.form.get("idFrontPicture").updateValueAndValidity();
    this.form.get("idBackPicture").updateValueAndValidity();
    this.form.get("moneySource").updateValueAndValidity();
    this.form.get("country").updateValueAndValidity();
  }

  private setIdPictureValidators() {
    const isRuc = this.selectedIdType === "ruc";
    const isPassport = this.selectedIdType === "passport";
    const isRetailWithLaft = !isRuc && this.isLAFTEnabled;

    if (isRetailWithLaft) {
      // LAFT enabled for retail investors (DNI/CE/Passport): No ID pictures needed
      this.form.get("idFrontPicture").clearValidators();
      this.form.get("idBackPicture").clearValidators();
    } else if (isRuc) {
      // RUC: No ID pictures needed (whether LAFT is enabled or not)
      this.form.get("idFrontPicture").clearValidators();
      this.form.get("idBackPicture").clearValidators();
    } else if (isPassport) {
      // Passport: Only front picture required
      this.form.get("idFrontPicture").setValidators([Validators.required]);
      this.form.get("idBackPicture").clearValidators();
    } else {
      // DNI or Carnet de Extranjería: Both pictures required
      this.form.get("idFrontPicture").setValidators([Validators.required]);
      this.form.get("idBackPicture").setValidators([Validators.required]);
    }
  }

  private setMoneySourceValidators() {
    const isRuc = this.selectedIdType === "ruc";
    const isRetailWithLaft = !isRuc && this.isLAFTEnabled;
    const isRucWithLaftJuridica = isRuc && this.isLAFTJuridicaEnabled;

    // moneySource is NOT required when:
    // 1. Retail investor (DNI/CE/Passport) with LAFT enabled
    // 2. RUC with LAFT Juridica enabled
    const shouldSkipMoneySource = isRetailWithLaft || isRucWithLaftJuridica;

    if (shouldSkipMoneySource) {
      this.form.get("moneySource").clearValidators();
    } else {
      // Legacy flow: moneySource is required
      this.form.get("moneySource").setValidators([Validators.required]);
    }
  }

  private setCountryValidators() {
    const isRuc = this.selectedIdType === "ruc";

    if (isRuc) {
      // RUC: Country/Nationality is not required
      this.form.get("country").clearValidators();
    } else {
      // DNI/CE/Passport: Country/Nationality is required
      this.form.get("country").setValidators([Validators.required]);
    }
  }

  sendEvent(event) {
    this.gtmService.newEvent(event);
  }

  get isLAFTEnabled(): boolean {
    return this.featureFlagService.isLAFTEnabled();
  }

  get isLAFTJuridicaEnabled(): boolean {
    return this.featureFlagService.isLAFTJuridicaEnabled();
  }

  /**
   * Determines if legacy registration sections (ID pictures, money source) should be visible
   *
   * Returns TRUE (show sections) when:
   * - RUC selected AND LAFT Juridica is disabled
   * - Retail investor (DNI/CE/Passport) AND LAFT is disabled
   *
   * Returns FALSE (hide sections) when:
   * - RUC selected AND LAFT Juridica is enabled
   * - Retail investor AND LAFT is enabled
   */
  get seeLegacyRegistration(): boolean {
    const isRuc = this.selectedIdType === 'ruc';

    if (isRuc) {
      return !this.isLAFTJuridicaEnabled;
    } else {
      return !this.isLAFTEnabled;
    }
  }
}
